import { SelectProps } from '@amzn/awsui-components-core';
import {
  Content,
  ContentAccessUser,
  ContentAudience,
  ContentMetadata,
  ContentNode,
  ContentNodeType,
  ContentRequest,
  ContentSubType,
  ContentTemplateType,
  ContentType,
  ContentTypeMetadata,
  ContentUserRole,
  ContentWorkflow,
  TechDomain,
  UserProfile,
  UserRoles,
  UserType,
  WorkflowState,
  Publication,
  BarRaiserTechDomains,
  RepoType,
} from '@amzn/awscat-pattern-library-service-model';
import moment from 'moment';
import rfdc from 'rfdc';
import join from 'join-array';
import BrowserHistory from '../components/utils/BrowserHistory';
import {
  AWSServiceOptions,
  ContentTemplateMap,
  ContentTypeToTemplateMap,
  CurrentTemplateVersionMap,
  DefaultContentSubTypeMap,
  EnvironementOptions,
  TechDomainOptions,
  WorkloadOptions,
} from '../components/utils/SelectableOptions';
import { ContentSchemaSection } from '../model/ContentSchemaSection';
import { FinalizingGraphState } from '../model/Enum';
import { workflowStateRankMap, workflowStateToFinalizingStateMap } from '../model/Map';
import UserTitle from '../model/UserTitle';
import { IMplServiceClient } from './IMplServiceClient';
import { MplServiceClientFactory } from './MplServiceClientFactory';
import { replaceNBSPWithSpace } from './ContentUtil';
const mplService: IMplServiceClient = MplServiceClientFactory?.create();

const clone = rfdc();

export const toTitleCase = (str: string) => {
  return str
    .replace(/[_-]/g, ' ')
    .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};

export const tablePaginationAriaLabels = {
  nextPageLabel: 'Next page',
  previousPageLabel: 'Previous page',
  // @ts-expect-error TS7006 - strict migration
  pageLabel: (pageNumber) => `Page ${pageNumber} of all pages`,
};

// @ts-expect-error TS7006 - strict migration
export const displayDate = (iso8601Date) => {
  return moment(iso8601Date).format('YYYY-MM-DD H:mm:ss (UTC)');
};

export const valueExist = (v: any) => {
  return v != null;
};

export const getUserRoles = (
  userProfile: UserProfile,
  skateHolders: { ownerId?: string; coAuthorIds?: string[]; peerReviewerIds?: string[] } = {},
) => {
  // tslint:disable-next-line:one-variable-per-declaration
  let isAdmin,
    isReviewer,
    isPublisher,
    isTechWriter,
    isPeerReviewer,
    isCoAuthors,
    isOwner,
    isBarRaiser = false;
  // convert roles set to array
  const userRoles = [...(userProfile?.roles ?? [])].map((role) => role.toLowerCase());
  isAdmin = userRoles.includes('admin');
  isReviewer = userRoles.includes('reviewer');
  isPublisher = userRoles.includes('publisher');
  isTechWriter = userRoles.includes('techwriter');
  isPeerReviewer = skateHolders?.peerReviewerIds?.includes(userProfile.userId);
  isCoAuthors = skateHolders?.coAuthorIds?.includes(userProfile.userId);
  isOwner = skateHolders?.ownerId === userProfile.userId;
  isBarRaiser = [...(userProfile?.contentRoles ?? [])].some(
    (cr) => cr.contentRole === ContentUserRole.BarRaiser,
  );
  return {
    isAdmin,
    isPrivilegedUser: isAdmin || isTechWriter || isReviewer || isPublisher,
    isSkateholder:
      isAdmin || isTechWriter || isReviewer || isPublisher || isPeerReviewer || isCoAuthors,
    isPublisher,
    isReviewer,
    isTechWriter,
    isPeerReviewer,
    isCoAuthors,
    isOwner,
    isBarRaiser,
  };
};

// @ts-expect-error TS7006 - strict migration
export const lastBreadcrumbClick = (e) => {
  e.preventDefault();
  BrowserHistory.push('/');
};

// @ts-expect-error TS7006 - strict migration
export const comparatorHelper = (item1, item2, fn) => {
  const a = fn(item1);
  const b = fn(item2);
  const collator = new Intl.Collator([], { sensitivity: 'base', numeric: true });

  if (noVal(a) || noVal(b) || noVal(a.props) || noVal(b.props) || noVal(a.type) || noVal(b.type)) {
    return collator.compare(a, b);
  }

  const aType = a.type.displayName || a.type.name;
  const bType = b.type.displayName || b.type.name;
  if (!aType || !bType || aType !== bType) {
    return collator.compare(aType, bType);
  }

  let compareProps;

  switch (aType) {
    case 'Button':
      compareProps = ['text'];
      break;
    case 'Checkbox':
      compareProps = ['checked', 'disabled'];
      break;
    case 'Icon':
      compareProps = ['name'];
      break;
    case 'Badge':
      compareProps = ['content'];
      break;
    case 'Link':
      compareProps = ['children'];
      break;
    default:
  }
  if (compareProps) {
    for (const p of compareProps) {
      const cmp = collator.compare(a.props[p], b.props[p]);
      if (!cmp) {
        continue;
      } else {
        return cmp;
      }
    }
  }

  return 0;
};

export const getCurrentTemplateInfo = (
  contentSubType: ContentSubType,
): { templateId: ContentTemplateType; templateVersion: number } => {
  const templateId = ContentTypeToTemplateMap.get(contentSubType);
  // @ts-expect-error TS2345 - strict migration
  let templateVersion = CurrentTemplateVersionMap.get(templateId);

  if (
    templateId === ContentTemplateType.MigrationPattern ||
    templateId === ContentTemplateType.CloudNativePattern ||
    templateId === ContentTemplateType.ProcessPattern ||
    templateId === ContentTemplateType.OptimizationPattern ||
    templateId === ContentTemplateType.GovernancePattern ||
    templateId === ContentTemplateType.SecurityAndCompliancePattern
  ) {
    templateVersion = 2;
  }

  return {
    // @ts-expect-error TS2322 - strict migration
    templateId,
    // @ts-expect-error TS2322 - strict migration
    templateVersion,
  };
};

export const getContentTemplateSchema = (
  contentType: ContentType,
  contentTypeMetadata: ContentTypeMetadata,
  section?: ContentSchemaSection,
) => {
  let { contentSubType } = contentTypeMetadata;
  if (!contentType) {
    return;
  }

  if (!contentSubType) {
    // @ts-expect-error TS2322 - strict migration
    contentSubType = DefaultContentSubTypeMap.get(contentType);
  }

  let { templateId, templateVersion } = contentTypeMetadata;

  if (!templateId) {
    const { templateId: currentTemplateId, templateVersion: currentTemplateVersionId } =
      getCurrentTemplateInfo(contentSubType);
    templateId = currentTemplateId;
    templateVersion = currentTemplateVersionId;
  }

  const schema =
    // @ts-expect-error TS2532 - strict migration
    ContentTemplateMap.get(templateId) && ContentTemplateMap.get(templateId).get(templateVersion);

  // @ts-expect-error TS2538 - strict migration
  return schema ? schema.definitions[section] : schema;
};

// @ts-expect-error TS7006 - strict migration
function noVal(v) {
  return typeof v === 'undefined' || v === null;
}

export const getFilterCounterText = (count: number) =>
  `${count} ${count === 1 ? 'match' : 'matches'}`;

export const getFilterCountOutOfTotalText = (count: number, total: number) =>
  `${count}/${total} ${count === 1 ? 'match' : 'matches'}`;

// @ts-expect-error TS7006 - strict migration
export const getHeaderCounterText = (items, selectedItems) => {
  return selectedItems && selectedItems.length > 0
    ? `(${selectedItems.length}/${items.length})`
    : `(${items.length})`;
};

export const getServerHeaderCounterText = (totalCount: any[], selectedItems: any[]) => {
  return selectedItems && selectedItems.length > 0
    ? `(${selectedItems.length}/${totalCount}+)`
    : `(${totalCount}+)`;
};

export const EDIT_COLLECTION_CHAR_LIMIT = 500;
export const NUMBER_OF_PUBLICATION_SEARCH_RESULTS = 3;

export const cardsPerRow = [
  {
    cards: 1,
  },
  {
    cards: 2,
    minWidth: 500,
  },
  {
    cards: 3,
    minWidth: 900,
  },
  {
    cards: 4,
    minWidth: 1200,
  },
  {
    cards: 5,
    minWidth: 1600,
  },
  {
    cards: 6,
    minWidth: 1920,
  },
];

export const cardsDefaultPageSize: number = 24;

export const maxCardSummaryLength: number = 180;

export const trimString = (str: string, maxLen: number): string => {
  return str && str.length > maxLen ? str.substring(0, maxLen) + '...' : str;
};

export const deepClone = (val: any) => {
  return clone(val);
};

// @ts-expect-error TS7006 - strict migration
export const promiseTimeout = async (time) => new Promise((resolve) => setTimeout(resolve, time));

export const addSpaceBetweenWords = (str: string): string =>
  str.replace(/([a-z0-9])([A-Z])/g, '$1 $2');

// Select the content state with the highest ranking for counting metrics.
export const getRelevantState = (contentWorkflow: ContentWorkflow) => {
  const { draftState, publicationWorkflows } = contentWorkflow;
  const stateHistory = [draftState].concat(publicationWorkflows.map((wf) => wf.state));
  // @ts-expect-error TS2345 - strict migration
  const highestRank = Math.min(...stateHistory.map((h) => workflowStateRankMap.get(h)));
  const relevantState = stateHistory.find((h) => workflowStateRankMap.get(h) === highestRank);
  return relevantState;
};

export const getFinalizingState = (state: WorkflowState, inTechWriterQueue: boolean) => {
  const finalizingState =
    state === WorkflowState.PUBLISHED && inTechWriterQueue
      ? FinalizingGraphState.Assigned
      : workflowStateToFinalizingStateMap.get(state);
  return finalizingState;
};

// Get the default selections given selection value
export const getDefaultFilterSelection = (id: string, label: string = id, value: string = id) => ({
  id,
  label,
  value,
});

export const getSelectOption = (value: string, label: string = value) => ({
  value,
  label,
});

export const extractUserAlias = (ownerId: string): string => {
  let ownerAlias = ownerId;
  const ownerIdSplit = ownerId ? ownerId.split('_') : [''];
  if (ownerIdSplit[0] === 'AmazonCorporate') {
    // @ts-expect-error TS2322 - strict migration
    ownerAlias = ownerIdSplit[1];
  }
  return ownerAlias;
};

export const getUserTypeFromId = (userId: string): UserType => {
  const prefix = userId.split('_')[0];
  switch (prefix) {
    case 'AmazonCorporate':
      return UserType.AWS;
    case 'APN':
      return UserType.Partner;
    default:
      return UserType.Customer;
  }
};

export const autoSuggestionMaxCount: number = 3;
export const autoSuggestionTitleWordLimit: number = 9;

export const maxSearchResultDescriptionLength: number = 400;

export const searchResultCardsDefaultPageSize: number = 12;

export const stringMetadataInputMinLength: number = 3;
export const stringMetadataInputMaxLength: number = 300;

export const longTitleInputMinLength: number = 20;
export const longTitleInputMaxLength: number = 100;

export const shortTitleInputMinLength: number = 20;
export const shortTitleInputMaxLength: number = 60;

export const nodeShortTitleInputMinLength: number = 0;
export const nodeShortTitleInputMaxLength: number = 60;

export const descriptionInputMinLength: number = 50;
export const descriptionInputMaxLength: number = 150;
export const descriptionTextareaRows: number = 6;

export const problemSolvedInputMinLength: number = 50;
export const problemSolvedInputMaxLength: number = 300;

export const proposalInputMinLength: number = 200;
export const proposalInputMaxLength: number = 1000;

export const duplicateSearchTimerDelay: number = 2000;

export const publicationTitleInputMaxLength: number = 300;

export const defaultNumberOfDuplicatesToShow = 5;

export const numberOfDuplicatesToAdd = 10;

export const defaultNumberOfContentsToShow = 5;

export const numberOfContentsToAdd = 5;

export const techDomainLimit = 10;

export const awsServiceLimit = 10;

export const publicationLimit = 10;

export const validProtocol = 'https://';

export const escapeSpecialCharacters = (text: string) =>
  text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const textMatches = (text: string, matchWord: string) => {
  // true if text matches with matchWord regardless of case
  return (
    (!text && !matchWord) || (text && matchWord && text.toLowerCase() === matchWord.toLowerCase())
  );
};

export const getUserTitleFromProfile = (
  userProfile: UserProfile,
  authorId: string,
  coAuthorIds: string[] = [],
  peerReviewerIds: string[] = [],
) => {
  const { userId, roles: systemRoles } = userProfile;
  let title = UserTitle.PeerReviewer;
  if (userId === authorId) {
    title = UserTitle.Author;
  } else if (Array.isArray(coAuthorIds) && coAuthorIds.includes(userId)) {
    title = UserTitle.CoAuthor;
    // @ts-expect-error TS18048 - strict migration
  } else if (systemRoles.has(UserRoles.Admin)) {
    title = UserTitle.Admin;
    // @ts-expect-error TS18048 - strict migration
  } else if (systemRoles.has(UserRoles.TechWriter)) {
    title = UserTitle.Techwriter;
    // @ts-expect-error TS18048 - strict migration
  } else if (systemRoles.has(UserRoles.Publisher)) {
    title = UserTitle.Publisher;
  } else if (Array.isArray(peerReviewerIds) && peerReviewerIds.includes(userId)) {
    title = UserTitle.PeerReviewer;
  }
  return title;
};

export const getEmptyContent = () => {
  const contentData = ContentNode.create('Home', '', ContentNodeType.Document);
  contentData.id = 'rootId';
  const content = Content.create(new ContentMetadata(), new ContentTypeMetadata(), {}, contentData);
  return content;
};

export const displayAuthorInfo = (ownerName: string, organizationName?: string) => {
  return organizationName ? `${ownerName} (${organizationName})` : ownerName;
};

export const displayByline = (
  owner: UserProfile,
  coAuthors: ContentAccessUser[] = [],
  workflow: ContentWorkflow | undefined,
) => {
  const ownerName = owner ? owner.name : workflow?.ownerProfile?.name;
  let ownerOrganization;
  if (owner?.type === UserType.AWS) {
    ownerOrganization = 'AWS';
  } else {
    ownerOrganization = owner ? owner.organizationName : workflow?.ownerProfile?.organizationName;
  }

  // @ts-expect-error TS2345 - strict migration
  let result = displayAuthorInfo(ownerName, ownerOrganization);
  for (const [idx, ca] of coAuthors.entries()) {
    const caDisplay = displayAuthorInfo(
      ca.userName,
      ca.userType === UserType.AWS ? 'AWS' : ca.organizationName,
    );
    const separator = idx === coAuthors.length - 1 ? ' and' : ',';
    result += `${separator} ${caDisplay}`;
  }

  return result;
};

export enum ContentRequestTagKey {
  MainTechnology = 'Main Technology',
  TechDomain = 'Tech Domain',
  WorkLoad = 'Workload',
  AwsServices = 'AWS Services',
  Environment = 'Environment',
}

const contentRequestTagToTemplateMetadataKeyMap = new Map<ContentRequestTagKey, string>([
  [ContentRequestTagKey.TechDomain, 'techDomains'],
  [ContentRequestTagKey.AwsServices, 'awsServices'],
  [ContentRequestTagKey.WorkLoad, 'workload'],
  [ContentRequestTagKey.MainTechnology, 'mainTechDomain'],
  [ContentRequestTagKey.Environment, 'environment'],
]);
const contetRequestMetadataSelectOptionsMap = new Map<ContentRequestTagKey, SelectProps.Option[]>([
  [ContentRequestTagKey.TechDomain, TechDomainOptions],
  [ContentRequestTagKey.AwsServices, AWSServiceOptions],
  [ContentRequestTagKey.WorkLoad, WorkloadOptions],
  [ContentRequestTagKey.MainTechnology, TechDomainOptions],
  [ContentRequestTagKey.Environment, EnvironementOptions],
]);

const setContentMetadataValue = (
  key: ContentRequestTagKey,
  value: string,
  contentMetaData: any,
) => {
  const contentMetadataKey = contentRequestTagToTemplateMetadataKeyMap.get(key);
  if (contentMetadataKey) {
    const contentMetadataValue =
      contetRequestMetadataSelectOptionsMap.get(key)?.find((option) => option.label === value)
        ?.value ?? value;
    if (key === ContentRequestTagKey.MainTechnology) {
      contentMetaData[contentMetadataKey] = contentMetadataValue;
    } else {
      contentMetaData[contentMetadataKey] = [
        ...(contentMetaData[contentMetadataKey] ?? []),
        contentMetadataValue,
      ];
    }
  }
};
// Convert content request tags to content template meta data
export const getTemplateMetadataFromContentRequest = (
  tags: Array<{ key: ContentRequestTagKey; value: string }>,
) => {
  const contentMetaData = {};
  tags.forEach((tag) => {
    if (Array.isArray(tag.value)) {
      tag.value.forEach((val) => {
        setContentMetadataValue(tag.key, val, contentMetaData);
      });
    } else {
      setContentMetadataValue(tag.key, tag.value, contentMetaData);
    }
  });
  return contentMetaData;
};

const getSelectOptionLabelByValue = (
  value: string,
  selectOptions?: SelectProps.Option[],
): string => {
  return selectOptions?.find((option) => option.value === value)?.label ?? value;
};

export const getContentRequestTagLabels = (
  tags: Array<{ key: string; value: string | string[] }>,
) => {
  const tagLabels = tags.map((tag) => {
    const { key, value } = tag;
    const keyEntry = [...contentRequestTagToTemplateMetadataKeyMap.entries()].find(
      ([entryLabel, entryValue]) => entryValue === key,
    );
    const keyLabel = keyEntry ? keyEntry[0] : key;
    const valueSelectOptions = keyEntry
      ? contetRequestMetadataSelectOptionsMap.get(keyEntry[0])
      : undefined;
    const valueLabels = Array.isArray(value)
      ? value.map((valueId) => getSelectOptionLabelByValue(valueId, valueSelectOptions))
      : getSelectOptionLabelByValue(value, valueSelectOptions);
    return {
      key: keyLabel,
      value: valueLabels,
    };
  });
  return tagLabels;
};

const joinValues = (values: string | string[]) => {
  return Array.isArray(values)
    ? join({
        array: values,
        separator: ', ',
        last: ' & ',
        max: 1,
        // @ts-expect-error TS7006 - strict migration
        maxMessage: (missed) => `(${missed} more...)`,
      })
    : values;
};

export const joinTagValues = (tags: Array<{ key: string; value: string | string[] }>) => {
  const joinedTags = tags.map((tag) => {
    const { key, value } = tag;
    const joinedValue = joinValues(value);
    return {
      key,
      value: joinedValue,
    };
  });
  return joinedTags;
};

export const isPublishedWorkflow = (workflow: ContentWorkflow): boolean => {
  const { publicationState } = workflow;
  // @ts-expect-error TS2345 - strict migration
  return isPublishedWorkflowState(publicationState);
};

export const canTechWriterEdit = (wf: ContentWorkflow) => {
  return (
    !wf.isLocked &&
    wf.draftState === WorkflowState.UNMODIFIED &&
    wf.contentAudience !== ContentAudience.INTERNAL
  );
};

export const isPendingToFinalizeWorkflow = (workflow: ContentWorkflow): boolean => {
  const { publicationState, skipDraftReview } = workflow;
  return !!(
    publicationState &&
    [WorkflowState.PUBLISHED, WorkflowState.FINALIZING].includes(publicationState) &&
    !skipDraftReview
  );
};

export const isPendingToDistributeWorkflow = (workflow: ContentWorkflow): boolean => {
  const { contentAudience, publicationState } = workflow;
  return !!(
    ContentAudience.PUBLIC === contentAudience &&
    publicationState &&
    [WorkflowState.FINALIZED].includes(publicationState)
  );
};

export const isRefinalizeWorkflow = (workflow: ContentWorkflow): boolean => {
  const { publicationState } = workflow;
  return !!(
    publicationState &&
    [WorkflowState.FINALIZED, WorkflowState.REVOKED, WorkflowState.DISTRIBUTED].includes(
      publicationState,
    )
  );
};

export const hasPreviousAPGWorkflows = (workflow: ContentWorkflow): boolean => {
  return (
    workflow.publicationWorkflows.filter((wf) =>
      [WorkflowState.FINALIZED, WorkflowState.DISTRIBUTED].includes(wf.state),
    ).length > 0
  );
};

export const findPublicationWorkflow = (contentWorkflow: ContentWorkflow) =>
  contentWorkflow.publicationWorkflows.find((wf) =>
    [
      WorkflowState.PUBLISHED,
      WorkflowState.FINALIZING,
      WorkflowState.FINALIZED,
      WorkflowState.REVOKED,
      WorkflowState.DISTRIBUTED,
    ].includes(wf.state),
  );

export const isPublishedWorkflowState = (publicationState: WorkflowState): boolean => {
  return (
    publicationState === WorkflowState.PUBLISHED ||
    publicationState === WorkflowState.FINALIZED ||
    publicationState === WorkflowState.DISTRIBUTED ||
    publicationState === WorkflowState.FINALIZING
  );
};

export const maxNumberOfPeerReviewers = 10;
export const allowedPatternAttachmentTypes = ['.pptx', '.docx', '.xlsx'];

export const searchEmployeeProfile = async (userId: string) => {
  let employeeProfile;
  try {
    const alias = userId.startsWith('AmazonCorporate') ? userId.split('_')[1] : userId;
    // @ts-expect-error TS2345 - strict migration
    const userProfiles = await mplService.searchUsers(alias);
    employeeProfile = userProfiles.find((u) => u.userId && u.userId === userId);
  } catch (e) {
    // tslint:disable-next-line: no-console
    console.error('Error to fetch user internal profile');
  }
  return employeeProfile;
};

export const getPublicationDisplayTitle = (publication: Publication) => {
  return replaceNBSPWithSpace(
    publication.contentMetadata.contentShortName || publication.contentMetadata.contentName,
  );
};

export const barRaiserMetadata = [
  ['mainTechDomain', BarRaiserTechDomains],
  ['techDomains', BarRaiserTechDomains],
];

export const contentRequestNeedsBarRaiser = (tagKey: ContentRequestTagKey, tagValue: string) => {
  const barRaiseMetadataTag = contentRequestTagToTemplateMetadataKeyMap.get(tagKey);
  return (
    barRaiseMetadataTag &&
    barRaiserMetadata.some(
      (metadata) =>
        // @ts-expect-error TS7053 - strict migration
        metadata[0] === barRaiseMetadataTag && metadata[1].includes(TechDomain[tagValue]),
    )
  );
};

export const isBarRaiserForSuggestion = (user: UserProfile, request: ContentRequest) => {
  const tags = request.contentRequestData.tags;
  if (
    !(
      user.contentRoles &&
      user.contentRoles.some((r) => r.contentRole === ContentUserRole.BarRaiser)
    )
  ) {
    return false;
  }
  const barRaiseMetadataTags =
    tags
      // @ts-expect-error TS7006 - strict migration
      ?.map((tag) => ({
        key: contentRequestTagToTemplateMetadataKeyMap.get(tag.key),
        value: tag.value,
      }))
      // @ts-expect-error TS7006 - strict migration
      .filter((tag) =>
        barRaiserMetadata.some(
          // @ts-expect-error TS7053 - strict migration
          (metadata) => metadata[0] === tag.key && metadata[1].includes(TechDomain[tag.value]),
        ),
      ) ?? [];
  // @ts-expect-error TS7006 - strict migration
  return barRaiseMetadataTags.some((tag) =>
    // @ts-expect-error TS18048 - strict migration
    user.contentRoles.some(
      (r) =>
        r.filter?.metadataAttributeName === tag.key &&
        r.filter?.metadataAttributeValue === tag.value,
    ),
  );
};

export const isPendingBarRaiserWorkflow = (workflow: ContentWorkflow): boolean => {
  const { draftState } = workflow;
  return !!(
    draftState &&
    [WorkflowState.REVIEWED, WorkflowState.PUBLISHER_REVIEW, WorkflowState.UNMODIFIED].includes(
      draftState,
    )
  );
};

export const isBarRaiserEditable = (workflow: ContentWorkflow): boolean => {
  const { publicationState } = workflow;
  const isInTechwriterPool = !!(
    publicationState &&
    [WorkflowState.PUBLISHED, WorkflowState.FINALIZING].includes(publicationState) &&
    workflow.contentAudience !== ContentAudience.INTERNAL
  );
  return isPendingBarRaiserWorkflow(workflow) || isInTechwriterPool;
};

export const convertReviewersNumberToText = (numberToConvert: number) => {
  if (numberToConvert <= 0) {
    return 0;
  }
  const reviewerNumbersTextArray = ['an', 'two', 'three', 'four', 'five', 'more than five'];
  return numberToConvert > reviewerNumbersTextArray.length
    ? reviewerNumbersTextArray[reviewerNumbersTextArray.length - 1]
    : reviewerNumbersTextArray[numberToConvert - 1];
};

export const waitForContentAvailablity = async (contentId: string) => {
  const MILLIS_IN_ONE_SECOND = 1000;
  let attempts = 0;
  while (attempts < 3) {
    await promiseTimeout(MILLIS_IN_ONE_SECOND);
    try {
      await mplService.getContent(contentId);
      break;
    } catch (e) {
      attempts++;
    }
  }
};

export const isPartnerContributed = (
  workflow: ContentWorkflow,
  contentCoauthorMap?: Map<string, string[]>,
) => {
  return (
    workflow?.ownerId?.startsWith('APN_') ||
    contentCoauthorMap?.get(workflow.contentId)?.some((userId) => userId.startsWith('APN_'))
  );
};

export const contentAudienceToRepoTypeMap = new Map<ContentAudience, RepoType>([
  [ContentAudience.INTERNAL, RepoType.Internal],
  [ContentAudience.EXTERNAL, RepoType.External],
  [ContentAudience.PUBLIC, RepoType.External],
]);
